import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import PageSEO from '@/components/base/PageSEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 pb-14 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900">文章不存在</h1>
          <Link to="/" className="text-primary-500 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 4);

  const category = categories.find((c) => c.slug === article.categorySlug);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    image: article.coverImage,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      '@type': 'Organization',
      name: '911吃瓜网',
      url: 'http://51cgwang.cn/',
    },
    publisher: {
      '@type': 'Organization',
      name: '911吃瓜网',
      url: 'http://51cgwang.cn/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://51cgwang.cn/article/${article.id}`,
    },
    articleSection: article.category,
    keywords: article.tags.join(','),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://51cgwang.cn/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: `http://51cgwang.cn/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '911吃瓜网的爆料内容可靠吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '911吃瓜网坚持"有据可查"的内容原则。所有独家爆料均经过多源核实，标注信息来源。对于暂未完全证实的传闻，我们会明确标注"待核实"标签，确保瓜友们在吃瓜的同时了解信息的可信度。',
        },
      },
      {
        '@type': 'Question',
        name: '如何向911吃瓜网投稿爆料？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '如果你掌握了一手网红黑料或明星八卦，欢迎通过"关于我们"页面的投稿渠道联系我们。我们严格保护爆料人隐私，所有投稿均采用匿名处理流程。',
        },
      },
      {
        '@type': 'Question',
        name: '911吃瓜网多久更新一次内容？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '911吃瓜网实行7×24小时全天候追踪机制，一旦有重大爆料事件发生，我们的编辑团队会在最短时间内核实并发布。日常内容每日更新不少于10篇。',
        },
      },
    ],
  };

  return (
    <>
      <PageSEO
        title={`${article.title} - 911吃瓜网`}
        description={article.summary}
        keywords={`911吃瓜网,${article.category},${article.tags.join(',')}`}
        canonicalUrl={`http://51cgwang.cn/article/${article.id}`}
        ogTitle={article.title}
        ogDescription={article.summary}
        ogImage={article.coverImage}
        articlePublishedTime={article.publishDate}
        articleModifiedTime={article.publishDate}
      />
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      <Navbar />

      <main>
        <section className="relative w-full h-[350px] md:h-[500px] overflow-hidden">
          <div className="absolute inset-0 mx-4 md:mx-6 my-3 md:my-4 rounded-3xl overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - 911吃瓜网`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/55"></div>
          </div>
          <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4 w-full">
            <span className="inline-block px-4 py-1.5 border border-white/40 text-white text-sm rounded-full mb-4 backdrop-blur-sm whitespace-nowrap">
              {article.category}
            </span>
            <h1 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-white leading-tight max-w-3xl mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-xs text-white/70">
              <span className="whitespace-nowrap">{article.publishDate}</span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-eye-line"></i>
                {article.views >= 10000
                  ? `${(article.views / 10000).toFixed(0)}万 阅读`
                  : `${article.views} 阅读`}
              </span>
              {article.isExclusive && (
                <span className="px-2 py-0.5 bg-primary-500 text-white text-xs rounded-full whitespace-nowrap">
                  独家
                </span>
              )}
            </div>
          </div>
        </section>

        <section className="py-10 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: article.category, path: `/category/${article.categorySlug}` },
                { name: article.title },
              ]}
            />

            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-secondary-50 text-secondary-700 text-xs rounded-full whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <article
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200">
              <h3 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                <i className="ri-share-forward-line text-primary-500"></i>
                分享这条爆料
              </h3>
              <div className="flex items-center gap-3">
                {[
                  { icon: 'ri-weibo-line', label: '微博' },
                  { icon: 'ri-wechat-line', label: '微信' },
                  { icon: 'ri-link', label: '复制链接' },
                ].map((item) => (
                  <button
                    key={item.icon}
                    className="flex items-center gap-1.5 px-4 py-2 border border-background-200 rounded-full text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-500 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className={item.icon}></i>
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关爆料
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              常见问题
            </h2>
            <div className="space-y-3">
              {[
                {
                  q: '911吃瓜网的爆料内容可靠吗？',
                  a: '911吃瓜网坚持"有据可查"的内容原则。所有独家爆料均经过多源核实，标注信息来源。对于暂未完全证实的传闻，我们会明确标注"待核实"标签，确保瓜友们在吃瓜的同时了解信息的可信度。',
                },
                {
                  q: '如何向911吃瓜网投稿爆料？',
                  a: '如果你掌握了一手网红黑料或明星八卦，欢迎通过"关于我们"页面的投稿渠道联系我们。我们严格保护爆料人隐私，所有投稿均采用匿名处理流程。',
                },
                {
                  q: '911吃瓜网多久更新一次内容？',
                  a: '911吃瓜网实行7×24小时全天候追踪机制，一旦有重大爆料事件发生，我们的编辑团队会在最短时间内核实并发布。日常内容每日更新不少于10篇。',
                },
              ].map((faq, i) => (
                <details
                  key={i}
                  className="group rounded-xl border border-background-200/70 bg-background-50 overflow-hidden"
                >
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}